import config from '../config';
import { jwtDecode } from 'jwt-decode';

// Helper function to get auth headers
const getAuthHeaders = (additionalHeaders = {}) => ({
    "Content-Type": "application/json",
    "Authorization": `Bearer ${localStorage.getItem('velaToken')}`,
    ...additionalHeaders
});

// Helper function to get user ID from token
const getUserIdFromToken = () => {
    const token = localStorage.getItem('velaToken');
    if (!token) return null;
    try {
        const decoded = jwtDecode(token);
        return decoded.sub;
    } catch (error) {
        console.error('Error decoding token:', error);
        return null;
    }
};

const handleResponse = async (response) => {
    if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error response:', errorText);
        throw new Error(errorText || `HTTP error! status: ${response.status}`);
    }
    return response.json();
};

const apiUtils = {
    // Client APIs
    fetchClient: async (clientId) => {
        const res = await fetch(`${config.api.url}/clients/${clientId}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    fetchChartEntries: async (clientId) => {
        const res = await fetch(`${config.api.url}/clients/${clientId}/chart-entries`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch chart entries");
        return res.json();
    },
    
    addChartEntry: async (clientId, entryData) => {
        const res = await fetch(`${config.api.url}/clients/${clientId}/chart-entries`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(entryData)
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
        return res.json();
    },
    
    updateClient: async (clientId, clientData) => {
        const res = await fetch(`${config.api.url}/clients/${clientId}`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(clientData)
        });
        return handleResponse(res);
    },

    addClient: async (midwifeId, clientData) => {
        const res = await fetch(`${config.api.url}/midwives/${midwifeId}/clients`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(clientData)
        });
        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Failed to create client");
        }
        return res.json();
    },

    // Pregnancy APIs
    fetchPregnancies: async (clientId) => {
        const res = await fetch(`${config.api.url}/clients/${clientId}/pregnancies`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    addPregnancy: async (clientId, pregnancyData) => {
        const res = await fetch(`${config.api.url}/clients/${clientId}/pregnancies`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(pregnancyData)
        });
        return handleResponse(res);
    },

    // Visit APIs
    fetchVisits: async (pregnancyId) => {
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyId}/prenatal-visits`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    createVisit: async (pregnancyId, visitData) => {
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyId}/prenatal-visits`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(visitData)
        });
        return handleResponse(res);
    },

    updateVisit: async (pregnancyId, visitId, visitData) => {
        const res = await fetch(`${config.api.url}/pregnancies/${pregnancyId}/prenatal-visits/${visitId}`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(visitData)
        });
        return handleResponse(res);
    },

    // Baby APIs
    addBaby: async (clientId, babyData) => {
        const res = await fetch(`${config.api.url}/clients/${clientId}/babies`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(babyData)
        });
        return handleResponse(res);
    },

    // Portal APIs
    sendPortalInvite: async (clientId) => {
        const res = await fetch(`${config.api.url}/clients/${clientId}/send-portal-invite`, {
            method: "POST",
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    }
};

export default apiUtils; 